import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import type { AuthSession } from "../api/types";
import { AppIcon } from "./AppIcon";
import "./workspace-topbar.css";
import "./stage3-workflow.css";
import "./workspace-topbar-unified.css";

export type WorkspaceKind = "home-style" | "construction";

export interface WorkspaceScreenSwitcher {
  value: string;
  options: Array<{ value: string; label: string }>;
  loading?: boolean;
  loadFailed?: boolean;
  onChange: (value: string) => void;
}

interface WorkspaceTopbarProps {
  activeWorkspace: WorkspaceKind;
  session: AuthSession;
  accountSession?: AuthSession;
  loggingOut: boolean;
  onLogout: () => Promise<void>;
  canAccessHomeStyle: boolean;
  homeStyleHref: string;
  constructionHref: string;
  onBlockedHomeStyle: () => void;
  searchLabel: string;
  searchPlaceholder: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSearchSubmit: (value: string) => void;
  onImprovement: () => void;
  improvementActive?: boolean;
  onAdmin?: () => void;
  adminActive?: boolean;
  status?: ReactNode;
  appearanceControls?: ReactNode;
  showWorkspaceSwitch?: boolean;
  showSearch?: boolean;
  showImprovement?: boolean;
  screenSwitcher?: WorkspaceScreenSwitcher;
}

function ScreenSwitcher({ switcher, mobile = false, onSelected }: { switcher: WorkspaceScreenSwitcher; mobile?: boolean; onSelected?: () => void }) {
  return (
    <label className={`project-selmo-screen-switcher${mobile ? " is-mobile" : ""}`}>
      <AppIcon name="admin" />
      <span>{mobile ? "로그인 화면 보기" : "화면 보기"}</span>
      <select
        aria-label="로그인 화면 보기"
        value={switcher.value}
        disabled={switcher.loading || switcher.loadFailed}
        title={switcher.loadFailed ? "담당자 목록을 불러오지 못했습니다." : "등록된 담당자의 로그인 화면 보기"}
        onChange={(event) => {
          switcher.onChange(event.target.value);
          onSelected?.();
        }}
      >
        {switcher.options.map((option) => <option value={option.value} key={option.value || "selmo-admin"}>{option.label}</option>)}
      </select>
    </label>
  );
}

export function WorkspaceTopbar({
  activeWorkspace,
  session,
  accountSession,
  loggingOut,
  onLogout,
  canAccessHomeStyle,
  homeStyleHref,
  constructionHref,
  onBlockedHomeStyle,
  searchLabel,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  onSearchSubmit,
  onImprovement,
  improvementActive = false,
  onAdmin,
  adminActive = false,
  status,
  appearanceControls,
  showWorkspaceSwitch = true,
  showSearch = true,
  showImprovement = true,
  screenSwitcher,
}: WorkspaceTopbarProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const moreTriggerRef = useRef<HTMLButtonElement>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const identitySession = accountSession ?? session;
  const identity = identitySession.name || identitySession.email || "팀 계정";

  useEffect(() => {
    if (!moreOpen) return;
    const frame = window.requestAnimationFrame(() => moreMenuRef.current?.querySelector<HTMLElement>('button:not([disabled]), select:not([disabled])')?.focus());
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMoreOpen(false);
        window.requestAnimationFrame(() => moreTriggerRef.current?.focus());
        return;
      }
      if (event.key !== "Tab") return;
      const items = [...(moreMenuRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), select:not([disabled])') ?? [])];
      const first = items[0];
      const last = items.at(-1);
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    const closeFromOutside = (event: PointerEvent) => {
      if (moreMenuRef.current?.contains(event.target as Node) || moreTriggerRef.current?.contains(event.target as Node)) return;
      setMoreOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("pointerdown", closeFromOutside);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("pointerdown", closeFromOutside);
    };
  }, [moreOpen]);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSearchSubmit(searchValue.trim());
  };

  const invokeUtility = (action: () => void) => {
    setMoreOpen(false);
    action();
  };


  return (
    <header className={`project-selmo-topbar hs-topbar selmo-topbar project-selmo-topbar--unified${appearanceControls ? " has-appearance-controls selmo-app-header" : ""}`}>
      <a className="project-selmo-topbar__brand hs-topbar-brand" href={activeWorkspace === "home-style" ? homeStyleHref : constructionHref} aria-label="프로젝트 셀모 홈">
        <span className="project-selmo-topbar__mark hs-topbar-mark selmo-brand-mark" aria-hidden="true">ㅅ</span>
        <span className="selmo-brand-copy"><strong>PROJECT SELMO</strong><small>업무 관리</small></span>
      </a>

      {showWorkspaceSwitch ? <nav className="project-selmo-topbar__switch hs-workspace-switch selmo-workspace-switch" aria-label="작업공간 전환">
        {canAccessHomeStyle ? (
          <a href={homeStyleHref} aria-current={activeWorkspace === "home-style" ? "page" : undefined}>홈스타일</a>
        ) : (
          <button type="button" aria-current={activeWorkspace === "home-style" ? "page" : undefined} onClick={onBlockedHomeStyle}>홈스타일</button>
        )}
        <a href={constructionHref} aria-current={activeWorkspace === "construction" ? "page" : undefined}>시공</a>
      </nav> : null}

      {showSearch ? <form className="project-selmo-topbar__search hs-global-search selmo-global-search" role="search" onSubmit={submitSearch}>
        <AppIcon name="projects" />
        <label className="sr-only" htmlFor="project-selmo-global-search">{searchLabel}</label>
        <input
          id="project-selmo-global-search"
          type="search"
          value={searchValue}
          placeholder={searchPlaceholder}
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </form> : null}

      {appearanceControls ? <div className="project-selmo-topbar__appearance">{appearanceControls}</div> : null}

      <div className="project-selmo-topbar__utilities hs-topbar-right selmo-header-actions">
        <div className="project-selmo-topbar__desktop-utilities">
          {showImprovement ? <button className={`hs-topbar-action${improvementActive ? " is-active" : ""}`} type="button" aria-label="개선센터" title="개선센터" onClick={onImprovement}>
            <AppIcon name="improvements" /><span>개선센터</span>
          </button> : null}
          {screenSwitcher ? <ScreenSwitcher switcher={screenSwitcher} /> : null}
          {onAdmin ? (
            <button className={`hs-topbar-action${adminActive ? " is-active" : ""}`} type="button" aria-label="관리" title="관리" onClick={onAdmin}>
              <AppIcon name="admin" /><span>관리</span>
            </button>
          ) : null}
          {status ? <div className="project-selmo-topbar__status hs-db-status"><i aria-hidden="true" />{status}</div> : null}
        </div>

        <button
          ref={moreTriggerRef}
          className="project-selmo-topbar__more"
          type="button"
          aria-haspopup="menu"
          aria-expanded={moreOpen}
          aria-controls="project-selmo-utility-menu"
          onClick={() => setMoreOpen((open) => !open)}
        >
          <AppIcon name="more" /><span className="sr-only">공통 메뉴</span>
        </button>

        <button className="project-selmo-topbar__account hs-account selmo-user-button" type="button" onClick={() => void onLogout()} disabled={loggingOut} aria-label={loggingOut ? "로그아웃 중" : `${identity} 로그아웃`}>
          {identitySession.picture ? <img src={identitySession.picture} alt="" referrerPolicy="no-referrer" /> : <span aria-hidden="true">{identity.slice(0, 1).toUpperCase()}</span>}
          <small>{loggingOut ? "로그아웃 중" : identity}</small>
          <em className="workspace-account-name" aria-hidden="true">{loggingOut ? "로그아웃 중" : identity}</em>
        </button>

        {moreOpen ? (
          <div ref={moreMenuRef} id="project-selmo-utility-menu" className="project-selmo-topbar__menu" role="menu">
            {showImprovement ? <button type="button" role="menuitem" onClick={() => invokeUtility(onImprovement)}><AppIcon name="improvements" />개선센터</button> : null}
            {screenSwitcher ? <ScreenSwitcher switcher={screenSwitcher} mobile onSelected={() => setMoreOpen(false)} /> : null}
            {onAdmin ? <button type="button" role="menuitem" onClick={() => invokeUtility(onAdmin)}><AppIcon name="admin" />관리</button> : null}
            {status ? <div className="project-selmo-topbar__menu-status">{status}</div> : null}
            <button type="button" role="menuitem" onClick={() => { setMoreOpen(false); void onLogout(); }} disabled={loggingOut}>로그아웃</button>
          </div>
        ) : null}
      </div>
    </header>
  );
}
